import { useEffect, useMemo, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { MagnifyingGlass, MapPin, ArrowClockwise, User, Shield } from "@phosphor-icons/react";

const ACTION_COLOR = {
  login: "bg-green-50 text-green-800 border-green-300",
  logout: "bg-gray-50 text-gray-800 border-gray-300",
  login_failed: "bg-red-50 text-red-800 border-red-300",
  backup_downloaded: "bg-blue-50 text-blue-800 border-blue-300",
  backup_restored: "bg-purple-50 text-purple-800 border-purple-300",
  general_settings_updated: "bg-indigo-50 text-indigo-800 border-indigo-300",
};
const actionCls = (a) => ACTION_COLOR[a] || "bg-muted text-foreground border-border";

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });
  } catch { return iso; }
}

export default function ActivityLogsTab() {
  const [tab, setTab] = useState("audit"); // "audit" | "logins"
  const [logs, setLogs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [msg, setMsg] = useState({ type: "", text: "" });

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "audit") {
        const params = new URLSearchParams();
        if (q) params.append("q", q);
        if (actionFilter) params.append("action", actionFilter);
        params.append("limit", "300");
        const r = await api.get(`/audit-logs?${params.toString()}`);
        setLogs(r.data.logs || []);
      } else {
        const r = await api.get("/admin/login-events?limit=300");
        setEvents(r.data.events || []);
      }
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const actionOpts = useMemo(() => {
    const s = new Set();
    logs.forEach(l => l.action && s.add(l.action));
    return Array.from(s).sort();
  }, [logs]);

  const mapUrl = (lat, lng) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=13/${lat}/${lng}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-1 border-b border-border">
        <button
          onClick={() => setTab("audit")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${tab === "audit" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="logs-tab-audit"
        >
          <Shield size={12} weight="bold" /> Audit trail
        </button>
        <button
          onClick={() => setTab("logins")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${tab === "logins" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="logs-tab-logins"
        >
          <MapPin size={12} weight="bold" /> Login events (with location)
        </button>
      </div>

      {msg.text && (
        <div className={`text-xs font-mono-mini uppercase tracking-widest px-3 py-2 border ${msg.type === "ok" ? "border-green-500 text-green-700 bg-green-50" : "border-red-500 text-red-700 bg-red-50"}`}>
          {msg.text}
        </div>
      )}

      {tab === "audit" && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search user, email, action…"
                className="w-full border border-border pl-9 pr-3 py-2 text-sm"
                data-testid="audit-search"
              />
            </div>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="border border-border px-2 py-2 text-sm"
              data-testid="audit-action-filter"
            >
              <option value="">All actions</option>
              {actionOpts.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <button onClick={load} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-1">
              <ArrowClockwise size={12} weight="bold" /> Refresh
            </button>
          </div>

          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr className="text-left">
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">Time</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">Who</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">Action</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">Resource</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">IP</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">Location</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground text-xs">Loading…</td></tr>
                )}
                {!loading && logs.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground text-xs">No events</td></tr>
                )}
                {logs.map(l => (
                  <tr key={l.id} className="border-t border-border hover:bg-muted/40">
                    <td className="px-3 py-2 font-mono-mini text-[11px] whitespace-nowrap">{fmtTime(l.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <User size={12} weight="bold" className="text-muted-foreground" />
                        <div>
                          <div className="font-semibold text-xs">{l.user_name || "—"}</div>
                          <div className="text-[10px] text-muted-foreground">{l.user_email || ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono-mini uppercase ${actionCls(l.action)}`}>
                        {l.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {l.resource_type ? <>
                        <span className="font-semibold">{l.resource_type}</span>
                        {l.resource_id && <span className="text-muted-foreground text-[10px] ml-1">#{String(l.resource_id).slice(0, 8)}</span>}
                      </> : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono-mini text-[10px] text-muted-foreground">{l.ip || "—"}</td>
                    <td className="px-3 py-2">
                      {l.lat != null && l.lng != null ? (
                        <a href={mapUrl(l.lat, l.lng)} target="_blank" rel="noreferrer" className="text-[#002FA7] underline text-[11px] flex items-center gap-1">
                          <MapPin size={10} weight="bold" /> {Number(l.lat).toFixed(3)}, {Number(l.lng).toFixed(3)}
                        </a>
                      ) : <span className="text-muted-foreground text-[10px]">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "logins" && (
        <>
          <div className="flex items-center gap-3">
            <button onClick={load} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-1">
              <ArrowClockwise size={12} weight="bold" /> Refresh
            </button>
            <div className="text-xs text-muted-foreground">
              Lat/lng is captured by the browser at sign-in if the user allowed geolocation. Nulls mean the user denied or the browser didn&apos;t send it.
            </div>
          </div>
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr className="text-left">
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">Time</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">User</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">Email</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">IP</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">Latitude</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">Longitude</th>
                  <th className="px-3 py-2 font-mono-mini uppercase text-[10px]">Map</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground text-xs">Loading…</td></tr>}
                {!loading && events.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground text-xs">No login events</td></tr>}
                {events.map(e => (
                  <tr key={e.id} className="border-t border-border hover:bg-muted/40">
                    <td className="px-3 py-2 font-mono-mini text-[11px] whitespace-nowrap">{fmtTime(e.created_at)}</td>
                    <td className="px-3 py-2 text-xs font-semibold">{e.user_name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{e.email}</td>
                    <td className="px-3 py-2 font-mono-mini text-[10px] text-muted-foreground">{e.ip || "—"}</td>
                    <td className="px-3 py-2 font-mono-mini text-[11px]">{e.lat != null ? Number(e.lat).toFixed(5) : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2 font-mono-mini text-[11px]">{e.lng != null ? Number(e.lng).toFixed(5) : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2">
                      {e.lat != null && e.lng != null ? (
                        <a href={mapUrl(e.lat, e.lng)} target="_blank" rel="noreferrer" className="text-[#002FA7] underline text-[11px]">Open</a>
                      ) : <span className="text-muted-foreground text-[10px]">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
